
import React, { useState } from 'react';
import { UserProfile } from '../../../app/domain/types/index.ts';
import { Search, MessageCircle, Book, FileText, ChevronRight, HelpCircle, Send, Paperclip } from 'lucide-react';

interface HelpSupportProps {
  user: UserProfile;
}

const HelpSupport: React.FC<HelpSupportProps> = ({ user }) => {
  const [activeView, setActiveView] = useState<'home' | 'ticket' | 'chat'>('home');

  const popularArticles = [
    { id: 1, title: 'How to reset your meal plan', category: 'Meal Planning' },
    { id: 2, title: 'Understanding your BMI calculation', category: 'Health Metrics' },
    { id: 3, title: 'Connecting with a professional', category: 'Consultations' },
    { id: 4, title: 'Managing your subscription', category: 'Billing' },
  ];

  return (
    <div className="h-full flex flex-col bg-[#FAFAFA] dark:bg-[#0f0f0f] relative">

      {/* Header */}
      <div className="p-6 md:p-8 pb-12 bg-blue-600 dark:bg-blue-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16"></div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h1 className="text-3xl font-bold mb-2">How can we help you?</h1>
          <p className="text-blue-100 mb-8">Search our knowledge base or get in touch.</p>

          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search for answers (e.g., 'cancel subscription', 'dietary preferences')"
              className="w-full pl-12 pr-4 py-4 rounded-2xl shadow-lg text-gray-900 focus:outline-none focus:ring-4 focus:ring-blue-500/30 transition-all"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 md:p-8 -mt-8 no-scrollbar">
        <div className="max-w-4xl mx-auto">

          {activeView === 'home' && (
            <div className="animate-fade-in-up">
              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                <button
                  onClick={() => setActiveView('ticket')}
                  className="p-6 rounded-2xl bg-white dark:bg-stone-800 shadow-md border border-stone-100 dark:border-stone-700 hover:-translate-y-1 transition-transform text-left group"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                    <FileText size={24} />
                  </div>
                  <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-1">Submit Ticket</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Get email support for complex issues.</p>
                </button>

                <button
                  onClick={() => setActiveView('chat')}
                  className="p-6 rounded-2xl bg-white dark:bg-stone-800 shadow-md border border-stone-100 dark:border-stone-700 hover:-translate-y-1 transition-transform text-left group"
                >
                  <div className="w-12 h-12 rounded-xl bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex items-center justify-center mb-4 group-hover:bg-green-600 group-hover:text-white transition-colors">
                    <MessageCircle size={24} />
                  </div>
                  <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-1">Live Chat</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Chat with a support agent instantly.</p>
                </button>

                <button className="p-6 rounded-2xl bg-white dark:bg-stone-800 shadow-md border border-stone-100 dark:border-stone-700 hover:-translate-y-1 transition-transform text-left group">
                  <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center mb-4 group-hover:bg-purple-600 group-hover:text-white transition-colors">
                    <Book size={24} />
                  </div>
                  <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-1">Knowledge Base</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Browse guides and tutorials.</p>
                </button>
              </div>

              {/* Popular Articles */}
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Popular Articles</h2>
              <div className="bg-white dark:bg-stone-800 rounded-2xl border border-stone-200 dark:border-stone-700 overflow-hidden mb-8">
                {popularArticles.map((article, i) => (
                  <div key={article.id} className={`p-4 flex items-center justify-between hover:bg-stone-50 dark:hover:bg-stone-700/50 cursor-pointer transition-colors ${i !== popularArticles.length - 1 ? 'border-b border-stone-100 dark:border-stone-700' : ''}`}>
                    <div className="flex items-center gap-4">
                      <HelpCircle size={20} className="text-gray-400" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">{article.title}</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{article.category}</p>
                      </div>
                    </div>
                    <ChevronRight size={18} className="text-gray-400" />
                  </div>
                ))}
              </div>

              {/* FAQ Section */}
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Frequently Asked Questions</h2>
              <div className="space-y-3">
                <details className="bg-white dark:bg-stone-800 rounded-xl border border-stone-200 dark:border-stone-700 p-4 group">
                  <summary className="font-medium cursor-pointer list-none flex justify-between items-center text-gray-900 dark:text-white">
                    Can I change my subscription plan later?
                    <ChevronRight className="group-open:rotate-90 transition-transform text-gray-400" size={18} />
                  </summary>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 leading-relaxed">Yes, you can upgrade or downgrade your plan at any time from the Billing settings page. Changes will take effect at the start of the next billing cycle.</p>
                </details>
                <details className="bg-white dark:bg-stone-800 rounded-xl border border-stone-200 dark:border-stone-700 p-4 group">
                  <summary className="font-medium cursor-pointer list-none flex justify-between items-center text-gray-900 dark:text-white">
                    How do I cancel my appointment?
                    <ChevronRight className="group-open:rotate-90 transition-transform text-gray-400" size={18} />
                  </summary>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 leading-relaxed">Go to the "Professional Help" tab, select "My Appointments", and click on the appointment you wish to cancel. Cancellations made more than 24 hours in advance are fully refundable.</p>
                </details>
              </div>
            </div>
          )}

          {activeView === 'ticket' && (
            <div className="animate-fade-in-up max-w-2xl mx-auto bg-white dark:bg-stone-800 p-8 rounded-3xl border border-stone-200 dark:border-stone-700 shadow-xl">
              <button onClick={() => setActiveView('home')} className="text-sm text-gray-500 hover:text-gray-900 mb-6 flex items-center gap-1"><ChevronRight className="rotate-180" size={14} /> Back to Help Center</button>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Submit a Request</h2>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Subject</label>
                  <input type="text" className="w-full p-3 bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl" placeholder="Brief description of the issue" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category</label>
                  <select className="w-full p-3 bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl">
                    <option>Technical Issue</option>
                    <option>Billing</option>
                    <option>Account</option>
                    <option>Feedback</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                  <textarea className="w-full p-3 bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl h-32" placeholder="Please describe your issue in detail..."></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Attachments</label>
                  <div className="border-2 border-dashed border-stone-200 dark:border-stone-700 rounded-xl p-6 text-center cursor-pointer hover:bg-stone-50 dark:hover:bg-stone-700/50 transition-colors">
                    <p className="text-sm text-gray-500">Drag files here or click to upload</p>
                  </div>
                </div>

                <button className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors shadow-lg">Submit Ticket</button>
              </div>
            </div>
          )}

          {activeView === 'chat' && (
            <div className="animate-fade-in-up h-[600px] bg-white dark:bg-stone-800 rounded-3xl border border-stone-200 dark:border-stone-700 shadow-xl flex flex-col overflow-hidden">
              <div className="p-4 bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-700 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400">
                    <MessageCircle size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white">Support Chat</h3>
                    <p className="text-xs text-green-500 flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-full"></span> Agent Online</p>
                  </div>
                </div>
                <button onClick={() => setActiveView('home')} className="text-gray-400 hover:text-gray-600"><ChevronRight className="rotate-180" size={20} /></button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                <div className="flex justify-start">
                  <div className="bg-stone-100 dark:bg-stone-700 text-gray-800 dark:text-white p-4 rounded-2xl rounded-tl-none max-w-[80%]">
                    Hello! How can I help you today?
                  </div>
                </div>
                <div className="flex justify-center text-xs text-gray-400 my-4">Today</div>
              </div>

              <div className="p-4 bg-stone-50 dark:bg-stone-900 border-t border-stone-200 dark:border-stone-700">
                <div className="flex items-center gap-2">
                  <button className="p-2 text-gray-400 hover:text-gray-600"><Paperclip size={20} /></button>
                  <input type="text" className="flex-1 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500/20" placeholder="Type a message..." />
                  <button className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700"><Send size={18} /></button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default HelpSupport;
